/**
 * Traffic
 * 
 * @Author: Focci
 * @Date: 2023-07-17 21:31:49
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-17 21:31:49
 */

// import PropTypes from 'prop-types'
import SVGLocation from '@img/location.svg'
import { CircularProgress } from '@comp/material'
import { fetchProxy } from '@lib/fetch/client'
import { PolyLine, fitBounds } from '@comp/mapbox'
import { useCallback, useState } from 'react'
import { Marker, useMap } from 'react-map-gl'
import { getDirections } from './lib'
import TrafficAutoComplete from './TrafficAutoComplete'
import TrafficType from './TrafficType'
import TrafficRecording from './TrafficRecording'
import { PanelScoff } from '../panel/PanelBody'

export function TrafficBody({
  locale = [], // [lng, lat]
}) {
  const { current } = useMap()
  const [recording, setRecording] = useState([])
  const [loading, setLoading] = useState(false)
  const [coords, setCoords] = useState([])
  const [proximity, setProximity] = useState('driving')
  const [endCoordinate, setEndCoordinate] = useState([])

  const getCoords = useCallback((endCoord, type) => {
    if(locale?.length === 2 && endCoord?.length === 2) {
      fetchProxy(
        getDirections(locale, [endCoord], type).then((d) => {
          setCoords(d.coordinates)
          fitBounds(current, d.coordinates, 100, false)
          
          setRecording((prev) => [{
            title: d.summary,
            coordinates: d.coordinates,
            proximity: type,
            endCoord,
            uuid: d.uuid,
            duration: Math.ceil(d.duration / 60)
          }, ...prev.slice(0, 2)])
        }),
        setLoading
      )
    }
  }, [current, locale])

  function handleAutocompleteChange(_e, val) {
    if(!val) return
    
    const { lng, lat } = val
    setEndCoordinate([lng, lat])
    getCoords([lng, lat], proximity)
  }
  
  function handleDragEnd(e) {
    const { lng, lat } = e.lngLat

    setEndCoordinate([lng, lat])
    getCoords([lng, lat], proximity)
  }

  function handleTrafficTypeChange(_e, val) {
    setProximity(val)
    getCoords(endCoordinate, val)
  }

  function handleRecordingClose({ index }) {
    setRecording((prev) => {
      const tmp = prev
      tmp.splice(index, 1)

      return [...tmp]
    })
  }

  function handleRecordingClick({ item }) {
    setCoords(item.coordinates)
    setProximity(item.proximity)
    setEndCoordinate(item.endCoord)
  }
  
  return (
    <>
      <div className="bg-white px-3 pb-3 w-72 max-w-full relative">
        <TrafficType 
          value={proximity}
          onChange={handleTrafficTypeChange}
        />
        <div className="my-2">
          <TrafficAutoComplete 
            onChange={handleAutocompleteChange}
          />
        </div>
        <TrafficRecording 
          data={recording}
          onClose={handleRecordingClose}
          onClick={handleRecordingClick}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <CircularProgress size={30} />
          </div>
        )}
      </div>
      {locale.length === 2 && (
        <Marker longitude={locale[0]} latitude={locale[1]}>
          <SVGLocation width="2rem" height="2rem" className="text-primary" />
        </Marker>
      )}
      {endCoordinate?.length === 2 && (
        <Marker
          draggable={!loading}
          longitude={endCoordinate[0]} 
          latitude={endCoordinate[1]}
          onDragEnd={handleDragEnd}
        >
          <SVGLocation width="2rem" height="2rem" className="text-error" />
        </Marker>
      )}
      {coords.length > 0 && <PolyLine data={coords} />}
    </>
  )
}

export default function Traffic({
  locale = [], // [lng, lat]
  ...panelProps
}) {
  return (
    <PanelScoff className="w-auto" delay={50000} {...panelProps}>
      <TrafficBody locale={locale} />
    </PanelScoff>
  )
}

// Traffic.propTypes = {
//   className: PropTypes.string,
// }
